import React, { useState, useEffect } from 'react';
import { useUserContext } from '@fff-web/fff-utilities';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SubscriptionUpdatePaymentModal } from '@fff-web/fff-payments';
import { trackPageLoad } from '@fff-web/fff-analytics';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useLocation } from 'react-router-dom';
import OptionButtons from '../TrialUpgrade/OptionButtons';
import PerksList from './PerksList';
import { couponScreenKey, Plans } from '../constants';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import useShowErrorOrRedirectThankYouPageV2 from '../../hooks/useShowErrorOrRedirectThankYouPageV2';
import {
  setDefaultPlan,
} from '../../helpers';
import Coupons from './Coupons';
import useCouponContent from '../../hooks/useCouponContent';
import useCouponSelector from '../../hooks/useCouponSelector';
import { getDefaultCouponsPlan } from '../../services/content';
import env from '../MyAccount/utils/env';
import WelcomeBack from './WelcomeBack';
import ReactivateDisclaimer from './ReactivateDisclaimer';
import { redirectToBaseSite } from '../../utils/redirectMethods';
import { useReactivateMyMembershipContext } from '../../utils/ContextProviders/ReactivateContext';
import './styles.scss';

const REACTIVATE_BUTTON_DEFAULT_COPY = 'Reactivate Me';

export const Reactivate = ({
  handleSubmit,
  isLoadingSubmit,
  ldClient,
  reactivateCaptchaRef
}) => {
  const { search, pathname } = useLocation();
  const { btnContainerRef, couponsApplied } = useReactivateMyMembershipContext();

  const flags = useFlags();
  const { userInfo } = useUserContext();
  const [selectedPlancode, setSelectedPlanCode] = useState(
    userInfo.user &&
    setDefaultPlan(
      userInfo.user.subscriptionState,
      userInfo.user.subscriptionType
    )
  );
  const myAccountPath =
    pathname && pathname.indexOf('/my-account-v3') === -1
      ? ''
      : '/my-account-v3';
  const thanksPage = `${myAccountPath}/reactivate-thanks`;
  const { displayAdditionalPerks } = flags || {};
  const subscriptionState =
    userInfo && userInfo.user ? userInfo.user.subscriptionState : '';
  const loggedIn = userInfo && userInfo.loggedIn;
  const { getCouponContentKey, handleCouponChange } = useCouponContent(
    couponScreenKey.REACTIVATE_EXPIRED_SUBSCRIPTION
  );
  useLaunchDarkly(ldClient, userInfo);

  const {
    handleApplyCoupon,
    handleRemoveCoupon,
    discount,
    couponCode,
  } = useCouponSelector(handleCouponChange);

  useEffect(() => {
    if (loggedIn && subscriptionState === 'EXPIRED') {
      trackPageLoad({
        properties: {
          coupon_code: couponCode,
          login_state: loggedIn ? 'logged in' : 'logged out',
        },
      }).then();
    }

    if (couponCode) {
      getDefaultCouponsPlan()
        .then((r) => {
          if (r.success) {
            if (
              Array.isArray(r.data.seasonal) &&
              r.data.seasonal.includes(couponCode)
            ) {
              setSelectedPlanCode(Plans.SEASONAL);
            }
            if (
              Array.isArray(r.data.annual) &&
              r.data.annual.includes(couponCode)
            ) {
              setSelectedPlanCode(Plans.ANNUAL);
            }
          }
        })
    }
  }, []);

  if (loggedIn && subscriptionState === 'CANCELED') {
    history.replace(`${myAccountPath}/reactivate-today${search}`);
    return null;
  }

  if (loggedIn && subscriptionState !== 'EXPIRED') {
    redirectToBaseSite();
    return null;
  }

  const handleChange = (membership) => {
    setSelectedPlanCode(membership);
  };

  const {
    showPayment,
    errorPaymentMessage,
    onClose,
  } = useShowErrorOrRedirectThankYouPageV2(history, thanksPage, couponsApplied);

  return (
    <>
      <div className="sectionsContainer">
        <WelcomeBack />
      </div>
      <div className="buttons-container" ref={btnContainerRef}>
        <OptionButtons
          flags={flags}
          handleChange={handleChange}
          selected={selectedPlancode}
          getCouponContentKey={getCouponContentKey}
        />
      </div>
      <div
        className="reactivate-list-container"
        data-testid="reactivateListContainer"
      >
        <PerksList
          membership={selectedPlancode}
          showAdditionalPerks={displayAdditionalPerks}
          getCouponContentKey={getCouponContentKey}
        />
      </div>
      <div className="body-container" data-testid="bodyContainer">
        <Coupons
          flags={flags}
          membership={selectedPlancode}
          couponCode={couponCode}
          couponsApplied={couponsApplied}
          handleApplyCoupon={handleApplyCoupon}
          handleRemoveCoupon={handleRemoveCoupon}
          handleCouponChange={handleCouponChange}
          couponDiscount={discount}
        />
        <ReactivateDisclaimer />
        <div className="reactivate-action text-center">
          <button
            type="button"
            className="btn btn-lg btn-cta"
            onClick={handleSubmit}
            disabled={isLoadingSubmit}
            data-testid="reactivate-btn"
          >
            {getCouponContentKey('buttonText', REACTIVATE_BUTTON_DEFAULT_COPY)}
          </button>
          <HCaptcha
            sitekey={env.hCaptchaSiteKey}
            onVerify={() => undefined}
            size="invisible"
            ref={reactivateCaptchaRef}
          />
        </div>
        <SubscriptionUpdatePaymentModal
          onClose={onClose}
          subtitle={errorPaymentMessage}
          isOpen={showPayment}
          onSuccess={handleSubmit}
          hasError={!!errorPaymentMessage}
        />
      </div>
    </>
  );
};


Reactivate.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  isLoadingSubmit: PropTypes.bool.isRequired,
  ldClient: PropTypes.string,
  reactivateCaptchaRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
};

export default Reactivate;
